import { Button } from "@/components/ui/button";
import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/router";
import { api } from "@/services/api";
import { trackEvent } from "@/services/eventTracker";
import PracticeBoard, { PuzzleAction, PuzzleReply } from "./PracticeBoard";
import useTranslation from "next-translate/useTranslation";

type SessionReply = PuzzleReply & {
  explanation?: string;
  outcome?: string;
  bestMove?: string;
  playedMove?: string;
};
type Session = {
  puzzle: {
    id: string;
    fen: string;
    gameId: string;
    gameUrl: string;
    moveIndex: number;
  };
  reply: SessionReply;
};

export default function SingleMistakePractice({
  puzzleId,
  practiceSessionId,
  entrySource,
}: {
  puzzleId: string;
  practiceSessionId: string;
  entrySource: string;
}) {
  const { t } = useTranslation("puzzles");
  const router = useRouter();
  const [session, setSession] = useState<Session | null>(null);
  const [reply, setReply] = useState<SessionReply | null>(null);
  const [error, setError] = useState("");
  const [retry, setRetry] = useState(0);
  const [finishedForToday, setFinishedForToday] = useState(false);
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    let cancelled = false;
    setError("");
    void api
      .post(
        `/review-puzzles/${encodeURIComponent(puzzleId)}/session`,
        {
          practiceSessionId,
          entrySource,
        },
        { timeout: 15000 },
      )
      .then((response) => {
        if (cancelled) return;
        setSession(response.data);
        setReply(response.data.reply);
      })
      .catch(() => {
        if (!cancelled)
          setError(
            t("mistakes.sessionOpenError"),
          );
      });
    return () => {
      cancelled = true;
      alive.current = false;
    };
  }, [puzzleId, practiceSessionId, entrySource, retry]);

  async function action(input: PuzzleAction) {
    const response = await api.post(
      `/review-puzzles/${puzzleId}/attempt`,
      {
        ...input,
        practiceSessionId,
      },
      { timeout: 15000 },
    );
    if (alive.current) setReply(response.data);
    return response.data;
  }

  function nextAction(action: string) {
    void trackEvent("practice_next_action", {
      practiceSessionId,
      puzzleId,
      gameId: session?.puzzle.gameId,
      action,
      entrySource,
      flowVersion: 1,
    });
  }

  if (error)
    return (
      <div role="alert" className="rounded-lg border bg-white p-6">
        <p>{error}</p>
        <Button
          variant="quiet"
          size="default"
          type="button"
          className="mt-3"
          onClick={() => setRetry((n) => n + 1)}
        >
          {t("general.retry")}
        </Button>
        <Link
          href="/puzzles/mistakes/practice"
          className="ml-5 underline"
        >
          {t("nav.mistakes")}
        </Link>
      </div>
    );
  if (!session) return <p role="status">{t("mistakes.opening")}</p>;
  if (finishedForToday)
    return (
      <div className="rounded-lg border border-athens-gray-200 bg-white p-6">
        <h2 className="text-xl font-semibold text-slate-900">
          {t("mistakes.doneToday")}
        </h2>
        <p className="mt-2 text-slate-600">
          {t("mistakes.oneSaved")}
        </p>
        <Button asChild variant="link" className="mt-5">
          <Link href="/play">{t("mistakes.backToChess")}</Link>
        </Button>
      </div>
    );
  const finished = !!(reply?.done || reply?.failed);
  return (
    <>
      <PracticeBoard
        layout="mistakes"
        nextLabel={t("mistakes.nextMistake")}
        onNext={() => {
          nextAction("next_mistake");
          void router.push("/puzzles/mistakes/practice");
        }}
        puzzle={session.puzzle}
        onAction={action}
        initialReply={session.reply}
        explanation={reply?.explanation}
      >
        <p className="mt-5 text-sm font-semibold text-slate-600">
          {t("mistakes.reviewedSession", { count: finished ? 1 : 0 })}
        </p>
        {finished && (
          <div
            className="mt-5 border-t border-athens-gray-200 pt-4"
            aria-live="polite"
          >
            <h3 className="font-semibold text-slate-900">
              {t("mistakes.onePracticed")}
            </h3>
            <p className="mt-2 text-sm text-slate-600">
              {t("mistakes.savedOptions")}
            </p>
            {reply.bestMove && (
              <p className="mt-2 text-sm text-slate-600">
                {reply.playedMove && <>{t("mistakes.inGame", { move: reply.playedMove })}{" "}</>}
                {t("mistakes.betterMove", { move: reply.bestMove })}
              </p>
            )}
            <div className="mt-4 flex flex-wrap gap-3">
              <Button asChild variant="quiet">
                <Link href="/play" onClick={() => nextAction("play_again")}>
                  {t("mistakes.playAgain")}
                </Link>
              </Button>
              <Button
                variant="neutral"
                size="default"
                type="button"
                onClick={() => {
                  nextAction("finish_for_today");
                  setFinishedForToday(true);
                }}
              >
                {t("mistakes.finishToday")}
              </Button>
            </div>
          </div>
        )}
        <p className="mt-5 border-t border-athens-gray-200 pt-4 text-sm text-slate-500">
          {t("mistakes.sourceRated", { move: Math.ceil(session.puzzle.moveIndex / 2) })}
        </p>
        <Button asChild variant="link" className="mt-2">
          <Link
            href={session.puzzle.gameUrl}
            onClick={() => nextAction("original_game")}
          >
            {t("mistakes.reviewOriginal")}
          </Link>
        </Button>
      </PracticeBoard>
    </>
  );
}
